/** Company-wide loan number prefix from GLOBAL_SETTINGS — hydrated at appInit / admin save. */
let loanNumberPrefix = ''

export const setLoanNumberPrefix = (prefix: string) => {
  loanNumberPrefix = typeof prefix === 'string' ? prefix : ''
}

export const getLoanNumberPrefix = () => loanNumberPrefix

export type LoanDisplayNoParts = {
  byteproFileName?: number | null
  displayNo?: string | null
  no?: number | null
  loanNumber?: number | null
}

/**
 * Human-facing loan number.
 * Formula: byteproFileName (> 0) || displayNo (non-empty) || `${prefix}${loanNumber}`
 */
export const getLoanDisplayNo = (parts: LoanDisplayNoParts): string => {
  if (parts.loanNumber === null) return ''
  const loanNumber = Number(parts.loanNumber ?? parts.no ?? 0)
  const bytepro = Number(parts.byteproFileName || 0)
  if (bytepro > 0) return String(bytepro)

  const displayNo = String(parts.displayNo ?? '').trim()
  if (displayNo) return displayNo

  return `${loanNumberPrefix}${loanNumber}`
}

/**
 * Display number for the loan open in the URL.
 * Redux keeps the previous loan until its overview arrives, and a slower response can
 * write that loan back after you have already moved on. Showing nothing until the store
 * matches the URL avoids painting the loan you just left.
 */
export const getOpenLoanDisplayNo = (loanDetail: LoanDisplayNoParts | null | undefined): string => {
  const routeLoanNumber = readRouteLoanNumber()
  if (!routeLoanNumber || !loanDetail) return ''
  const openLoanNumber = loanDetail.loanNumber ?? loanDetail.no
  if (String(openLoanNumber ?? '') !== String(routeLoanNumber)) return ''
  return getLoanDisplayNo(loanDetail)
}

/** Loan number in the URL. Does not write the shared request header. */
export function readRouteLoanNumber(): string {
  const { href } = window.location
  if (href.endsWith('/new') || href.includes('/new/') || href.includes('/pool/')) return ''
  const params = href.split('?')[0].split('/')
  const loanNumber = params[params.length - 1]
  if (isNaN(Number(loanNumber))) return ''
  return loanNumber
}

/** If value starts with the cached prefix, return the remainder; otherwise return value unchanged. */
export const stripLoanNumberPrefix = (value: string): string => {
  const raw = String(value ?? '')
  if (!loanNumberPrefix || !raw.startsWith(loanNumberPrefix)) return raw
  return raw.slice(loanNumberPrefix.length)
}
